import { useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { Edit, PlayArrow } from "@mui/icons-material";
import {
  defaultSiteEffectsConfig,
  SiteEffectsConfig,
  SiteEffectsFrameworkDialog,
  SiteEffectsOverlaySession,
} from "./MemberSiteEffectsFramework";

interface SiteEffectsSettingsPanelProps {
  /** Number of sessions required (shown when showCount is true). */
  count?: number;
  onCountChange?: (count: number) => void;
  countLabel?: string;
  countHelperText?: string;
  showCount?: boolean;

  /** Session duration in seconds. */
  durationSec: number;
  onDurationChange: (durationSec: number) => void;

  /** Site FX content/behaviour config. */
  config: SiteEffectsConfig;
  onConfigChange: (config: SiteEffectsConfig) => void;

  disabled?: boolean;
}

/**
 * Reusable panel for configuring Site FX count, duration, content settings
 * and a test trigger.  Used in both ConfigureView (schedule day settings) and
 * EditGameDialog (Snakes & Ladders hypnosis settings).
 */
export default function SiteEffectsSettingsPanel({
  count,
  onCountChange,
  countLabel = "Times for this day",
  countHelperText = "How many Site FX sessions must be completed",
  showCount = true,
  durationSec,
  onDurationChange,
  config,
  onConfigChange,
  disabled = false,
}: SiteEffectsSettingsPanelProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testVideoTrigger, setTestVideoTrigger] = useState(0);

  return (
    <Stack spacing={1.25} sx={{ maxWidth: 520 }}>
      {showCount && onCountChange && (
        <TextField
          size="small"
          type="number"
          label={countLabel}
          value={count ?? 1}
          disabled={disabled}
          onChange={(e) => onCountChange(Math.max(0, Number(e.target.value) || 0))}
          inputProps={{ min: 0, step: 1 }}
          helperText={countHelperText}
        />
      )}
      <TextField
        size="small"
        type="number"
        label="Duration (min)"
        value={Math.max(1, Math.round(durationSec / 60))}
        disabled={disabled}
        onChange={(e) => onDurationChange(Math.max(60, (Number(e.target.value) || 1) * 60))}
        inputProps={{ min: 1, step: 1 }}
        helperText="Length of each Site FX run in minutes. Members cannot close early once started."
      />
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit />}
          disabled={disabled}
          onClick={() => setSettingsOpen(true)}
        >
          Settings
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlayArrow />}
          disabled={disabled}
          onClick={() => {
            setTestOpen(true);
            setTestVideoTrigger((n) => n + 1);
          }}
        >
          Test
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Test opens the Site FX overlay and starts a video immediately.
      </Typography>

      <SiteEffectsFrameworkDialog
        open={settingsOpen}
        armed={false}
        config={config}
        onConfigChange={onConfigChange}
        onClose={() => setSettingsOpen(false)}
        onActivate={() => { }}
        onDeactivate={() => { }}
        onTestVideo={() => {
          setTestOpen(true);
          setTestVideoTrigger((n) => n + 1);
        }}
        showActivationControls={false}
      />
      <SiteEffectsOverlaySession
        open={testOpen}
        ownerTest
        title="Site FX Test"
        config={config}
        testVideoTrigger={testVideoTrigger}
        onClose={() => setTestOpen(false)}
      />
    </Stack>
  );
}

/** Cast a stored plain object back to SiteEffectsConfig (safe default if undefined). */
export function toSiteEffectsConfig(raw: unknown): SiteEffectsConfig {
  return (raw as SiteEffectsConfig | undefined) ?? defaultSiteEffectsConfig;
}
